import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Font,
    Link,
} from '@react-pdf/renderer';
import moment from 'moment';
import {
    FundRequest,
    TransactionDefault,
    TransactionTravel,
    TransactionFood,
    TransactionUpload,
} from '~/types/fundrequests';

type Transaction = TransactionDefault | TransactionTravel | TransactionFood;

// Register a font to ensure text renders
Font.register({
    family: 'Helvetica',
    fonts: [
        {
            src: 'https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyCg4TYFv.ttf',
        },
        {
            src: 'https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyCg4QYFv.ttf',
            fontWeight: 'bold',
        },
    ],
});

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontFamily: 'Helvetica',
    },
    title: {
        fontSize: 24,
        marginBottom: 10,
        fontFamily: 'Helvetica',
    },
    table: {
        width: '100%',
        marginBottom: 10,
        breakInside: 'avoid',
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 5,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    categoryRow: {
        backgroundColor: '#F3F4F6',
        paddingVertical: 5,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    categoryTitle: {
        fontSize: 14,
        fontWeight: 'bold',
    },
    tableHeader: {
        flex: 1,
        fontWeight: 'bold',
        fontSize: 12,
    },
    tableCell: {
        flex: 1,
        fontSize: 10,
    },
    total: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        paddingVertical: 5,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    totalLabel: {
        fontWeight: 'bold',
        fontSize: 12,
        marginRight: 10,
    },
    totalAmount: {
        fontSize: 12,
    },
    files: {
        marginLeft: 20,
        marginBottom: 5,
    },
    fileLabel: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 2,
    },
    file: {
        fontSize: 10,
        marginLeft: 5,
        marginTop: 2,
    },
});

interface ExpensesTableProps {
    fundRequest: FundRequest;
}

export const ExpensesTable = ({ fundRequest }: ExpensesTableProps) => {
    const {
        transactions_medical = [],
        transactions_medications = [],
        transactions_travel = [],
        transactions_caregiving = [],
        transactions_physical_therapy = [],
        transactions_food = [],
        transactions_insurance = [],
        transactions_relocation_lodging = [],
        transactions_other = [],
    } = fundRequest;

    const getCategoryTotal = (transactions: Transaction[]): number => {
        return transactions.reduce((sum, transaction) => sum + Number(transaction.amount || 0), 0);
    };

    const getTransactionDescription = (transaction: Transaction): string => {
        if ('description' in transaction) {
            return transaction.description;
        } else if ('destination' in transaction) {
            return `Travel to ${transaction.destination}`;
        } else if ('recipient_name' in transaction) {
            return `Food for ${transaction.recipient_name}`;
        }
        return 'No description available';
    };

    const getTransactionDate = (transaction: Transaction): string => {
        if ('expense_date' in transaction) {
            return transaction.expense_date;
        } else if ('date_of_travel' in transaction) {
            return transaction.date_of_travel;
        } else if ('start_date' in transaction) {
            return transaction.start_date;
        }
        return '';
    };

    const renderTransaction = (transaction: Transaction) => (
        <View key={transaction.id}>
            <View style={styles.tableRow}>
                <Text style={styles.tableCell}>{getTransactionDescription(transaction)}</Text>
                <Text style={styles.tableCell}>
                    {moment(getTransactionDate(transaction)).format('MM/DD/YYYY')}
                </Text>
                <Text style={styles.tableCell}>
                    ${Number(transaction.amount || 0).toFixed(2)}
                </Text>
            </View>
            <View style={styles.files}>
                {transaction.uploads?.map((item: TransactionUpload) => (
                    <Link
                        key={item.id}
                        style={styles.file}
                        src={item.image_url}
                    >
                        {item.image_url}
                    </Link>
                ))}
            </View>
        </View>
    );

    const renderCategory = (title: string, transactions: Transaction[]) => {
        if (!transactions.length) return null;

        return (
            <View key={title}>
                <View style={styles.categoryRow}>
                    <Text style={styles.categoryTitle}>{title}</Text>
                </View>
                {transactions.map(renderTransaction)}
                <View style={styles.total}>
                    <Text style={styles.totalLabel}>Total {title}:</Text>
                    <Text style={styles.totalAmount}>
                        ${getCategoryTotal(transactions).toFixed(2)}
                    </Text>
                </View>
            </View>
        );
    };

    return (
        <View style={styles.table}>
            <View style={styles.tableRow}>
                <Text style={styles.tableHeader}>Description</Text>
                <Text style={styles.tableHeader}>Date</Text>
                <Text style={styles.tableHeader}>Amount</Text>
            </View>
            {renderCategory('Medical', transactions_medical)}
            {renderCategory('Medications', transactions_medications)}
            {renderCategory('Travel/Mileage', transactions_travel)}
            {renderCategory('Caregiving', transactions_caregiving)}
            {renderCategory('Physical Therapy', transactions_physical_therapy)}
            {renderCategory('Food/Incidentals', transactions_food)}
            {renderCategory('Insurance', transactions_insurance)}
            {renderCategory('Relocation/Lodging', transactions_relocation_lodging)}
            {renderCategory('Other', transactions_other)}

            <View style={[styles.total, { borderTopWidth: 2, borderTopColor: '#000' }]}>
                <Text style={styles.totalLabel}>Grand Total:</Text>
                <Text style={styles.totalAmount}>
                    $
                    {(
                        getCategoryTotal(transactions_medical) +
                        getCategoryTotal(transactions_medications) +
                        getCategoryTotal(transactions_travel) +
                        getCategoryTotal(transactions_caregiving) +
                        getCategoryTotal(transactions_physical_therapy) +
                        getCategoryTotal(transactions_food) +
                        getCategoryTotal(transactions_insurance) +
                        getCategoryTotal(transactions_relocation_lodging) +
                        getCategoryTotal(transactions_other)
                    ).toFixed(2)}
                </Text>
            </View>
        </View>
    );
};
